import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Hinzufuegen und Entfernen laufen bewusst ueber unterschiedliche
 * Endpunkte, mit unterschiedlicher Nachbehandlung:
 *
 * - addGift: /cart/add.js - einzig dieser Endpunkt kann ein Produkt
 *   hinzufuegen, das noch NICHT im Warenkorb liegt. Die Antwort enthaelt
 *   aber nur das neue Item, nicht den vollstaendigen Warenkorb - deshalb
 *   wird danach separat frisch /cart.js abgerufen UND durch
 *   createCartFromAjaxResponse() umgewandelt, bevor es an andere
 *   Theme-Komponenten weitergereicht wird.
 *
 * - removeGift: /cart/change.js - hier existiert die Zeile bereits, die
 *   Antwort ist bereits der vollstaendige Warenkorb.
 *
 * reached/giftPresent/giftKey/giftSourceAuto werden NICHT selbst
 * berechnet, sondern direkt aus dem von Liquid gelieferten JSON-Block
 * gelesen (siehe invisiwear-cart-progress.liquid) - das ist die einzige
 * Quelle der Wahrheit fuer den Schwellenwert-Status.
 */

let busy = false;

function getConfig() {
  const el = document.getElementById('invisiwear-gwp-config');
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (error) {
    console.warn('[invisiwear-cart-rewards] Konnte Konfiguration nicht parsen:', error);
    return null;
  }
}

function extractConfigFromSections(sections) {
  if (!sections) return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string' || !html.includes('invisiwear-gwp-config')) continue;
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.getElementById('invisiwear-gwp-config');
      if (el?.textContent) return JSON.parse(el.textContent);
    } catch (error) {
      console.warn('[invisiwear-cart-rewards] Konnte Konfiguration aus Section nicht parsen:', error);
    }
  }
  return null;
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

async function fetchCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart-Abruf fehlgeschlagen: ${response.status}`);
  return response.json();
}

async function submitCartChange(action, url, body, sections, needsFreshCartForConversion) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: [],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(url, fetchConfig('json', { body }));
    const parsed = await response.json();

    if (parsed.errors) {
      console.warn('[invisiwear-cart-rewards] Warenkorb-Update mit Fehlern:', parsed.errors);
      deferred.reject(new Error(JSON.stringify(parsed.errors)));
      return;
    }

    for (const sectionId of sections) {
      if (parsed.sections?.[sectionId]) {
        morphSection(sectionId, parsed.sections[sectionId], { mode: 'hydration' });
      }
    }

    let resolvedCart;
    try {
      const cartShapedJson = needsFreshCartForConversion ? await fetchCart() : parsed;
      resolvedCart = CartLinesUpdateEvent.createCartFromAjaxResponse(cartShapedJson);
    } catch (conversionError) {
      console.warn('[invisiwear-cart-rewards] Cart-Konvertierung fehlgeschlagen:', conversionError);
      deferred.reject(conversionError);
      return;
    }

    deferred.resolve({
      cart: resolvedCart,
      detail: { sections: parsed.sections, source: 'invisiwear-cart-rewards', didError: false },
    });
  } catch (error) {
    deferred.reject(error);
    console.warn('[invisiwear-cart-rewards] Warenkorb-Update fehlgeschlagen:', error);
  }
}

async function addGift(key, milestone) {
  const sections = sectionIds();
  const body = JSON.stringify({
    id: milestone.giftVariantId,
    quantity: 1,
    properties: { _gwp_gift: key, _gwp_source: 'auto' },
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });

  const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
  await submitCartChange('add', url, body, sections, true);
}

async function removeGift(key) {
  const sections = sectionIds();
  const body = JSON.stringify({
    id: key,
    quantity: 0,
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });

  const url = window.Theme?.routes?.cart_change_url || '/cart/change.js';
  await submitCartChange('remove', url, body, sections, false);
}

async function evaluateMilestone(key, milestone) {
  if (!milestone.enabled) return;

  // Entfernen muss unabhaengig von Modus/Auto-Add greifen - sonst bleibt
  // ein bereits automatisch hinzugefuegtes Geschenk haengen, sobald diese
  // Einstellungen nachtraeglich geaendert werden.
  if (!milestone.reached && milestone.giftPresent && milestone.giftSourceAuto) {
    await removeGift(milestone.giftKey);
    return;
  }

  if (milestone.mode !== 'auto' || !milestone.autoAdd) return;
  if (!milestone.giftVariantId) return;

  if (milestone.reached && !milestone.giftPresent) {
    await addGift(key, milestone);
  }
}

async function runMilestones(config) {
  if (!config?.milestones) return;
  for (const [key, milestone] of Object.entries(config.milestones)) {
    await evaluateMilestone(key, milestone);
  }
}

async function handleMutation(detail) {
  if (busy) return;
  busy = true;
  try {
    const config = extractConfigFromSections(detail?.sections) || getConfig();
    await runMilestones(config);
  } catch (error) {
    console.warn('[invisiwear-cart-rewards] Auswertung fehlgeschlagen:', error);
  } finally {
    busy = false;
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.target === document) return;

  event.promise
    ?.then(({ detail } = {}) => handleMutation(detail))
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[invisiwear-cart-rewards] Event promise rejected:', error);
      }
    });
});

(async () => {
  if (busy) return;
  busy = true;
  try {
    await runMilestones(getConfig());
  } finally {
    busy = false;
  }
})();